import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectDevices } from '../../../../../stores/reducers/devicesData/selector';
import { iFullStoreState } from '../../../../../shared/interfaces';
import { calculateDaysLeft, groupExpirations, IExpirationItem, IExpirationGroups } from '../utils';

export const useExpirationsReport = (deviceIdsFilter: string[] = []) => {
    const [loading, setLoading] = useState(true);
    const devicesDetails = useSelector(selectDevices);
    const allExtraInfo = useSelector((state: iFullStoreState) => state.general.extraInfo);

    const reportData = useMemo<IExpirationGroups | null>(() => {
        if (!devicesDetails || !allExtraInfo) return null;

        const fieldNames = [
            'Registration',
            'DOT Insp Exp.',
            'Rail Insp Exp.',
            'Aerial Insp Exp.',
            'Crane Cert Reg',
            'Crane Cert',
            'Emissions',
            'Apportioned',
            '2290 Hwy Tax',
            'Warranty Ext.',
            'Tire/Wheel Wty'
        ];
        const fieldKeyMap: Record<string, { key: string; name: string }> = {};

        // Find all the field keys we're interested in
        for (const [key, info] of Object.entries(allExtraInfo)) {
            const fieldName = info?.details?.name;
            if (fieldName && fieldNames.includes(fieldName)) {
                fieldKeyMap[fieldName] = { key, name: fieldName };
            }
        }

        if (Object.keys(fieldKeyMap).length === 0) {
            return groupExpirations([]);
        }

        const items: IExpirationItem[] = [];
        const devicesArray = devicesDetails.valueSeq().toArray();

        const targetDevices = deviceIdsFilter.length > 0
            ? devicesArray.filter(d => deviceIdsFilter.includes(d.id) && !d.isArchived)
            : devicesArray.filter(d => !d.isArchived);

        targetDevices.forEach(device => {
            const extraInfo = device['extra-info'];
            if (!extraInfo) return;

            // Check each field type
            for (const [fieldName, { key, name }] of Object.entries(fieldKeyMap)) {
                const fieldData = extraInfo[key];
                const dateStr = typeof fieldData === 'object' && fieldData !== null ? fieldData.date : null;

                if (dateStr) {
                    const daysLeft = calculateDaysLeft(dateStr);

                    if (daysLeft <= 90) {
                        items.push({
                            deviceId: device.id,
                            vehicleName: device.name,
                            itemName: name,
                            expireDate: dateStr,
                            daysLeft
                        });
                    }
                }
            }
        });

        return groupExpirations(items);
    }, [devicesDetails, allExtraInfo, deviceIdsFilter]);

    useEffect(() => {
        if (devicesDetails && allExtraInfo) {
            setLoading(false);
        }
    }, [devicesDetails, allExtraInfo]);

    return { reportData, loading };
};